import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Chip, CircularProgress, Divider, List, ListItem, ListItemText, TextField, Button, Switch, FormControlLabel, MenuItem, InputAdornment, IconButton, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Tooltip } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { adminAPI, ordersAPI } from '../../services/api';
import dayjs from 'dayjs';
import { Delete as DeleteIcon, Launch as LaunchIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';

const SuperAdminUserDetailPage = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);
    const [user, setUser] = useState(null);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [originalUser, setOriginalUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const navigate = useNavigate();
    const [ordersPage, setOrdersPage] = useState(0);
    const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersData, setOrdersData] = useState([]);
    const [ordersTotal, setOrdersTotal] = useState(0);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [ovRes, userRes] = await Promise.all([
                    adminAPI.getUserOverview(id),
                    adminAPI.getUser(id),
                ]);
                setOverview(ovRes.data);
                setUser(userRes.data.user);
                setOriginalUser(userRes.data.user);
            } catch (e) {
                toast.error('Failed to load user overview');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    const fetchUserOrders = async (page = 0, rows = ordersRowsPerPage) => {
        try {
            setOrdersLoading(true);
            const tzOffsetMinutes = new Date().getTimezoneOffset();
            const res = await ordersAPI.getOrders({ page: page + 1, limit: rows, userId: id, tzOffsetMinutes });
            setOrdersData(res.data.orders || []);
            setOrdersTotal(res.data.pagination?.totalOrders || 0);
        } catch (e) {
            toast.error('Failed to load user orders');
        } finally {
            setOrdersLoading(false);
        }
    };

    useEffect(() => {
        fetchUserOrders(0, ordersRowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onOrdersPageChange = (_e, newPage) => {
        setOrdersPage(newPage);
        fetchUserOrders(newPage, ordersRowsPerPage);
    };

    const onOrdersRowsChange = (e) => {
        const rows = parseInt(e.target.value, 10);
        setOrdersRowsPerPage(rows);
        setOrdersPage(0);
        fetchUserOrders(0, rows);
    };

    const deleteOrder = async (orderId) => {
        try {
            await ordersAPI.deleteOrder(orderId);
            toast.success('Order deleted');
            fetchUserOrders(ordersPage, ordersRowsPerPage);
        } catch (e) {
            toast.error('Failed to delete order');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (!overview || !user) return null;

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phone: user.phone,
                company: user.company,
                profileImage: user.profileImage,
                emailVerified: user.emailVerified,
                isActive: user.isActive,
                role: user.role,
                address: user.address,
                settings: user.settings,
            };
            // Only include password if provided and valid
            if (newPassword || confirmPassword) {
                if (newPassword !== confirmPassword) {
                    toast.error('Passwords do not match');
                    setSaving(false);
                    return;
                }
                if (newPassword.length < 6) {
                    toast.error('Password must be at least 6 characters');
                    setSaving(false);
                    return;
                }
                payload.newPassword = newPassword;
            }
            const res = await adminAPI.updateUser(id, payload);
            const updated = res.data?.user || user;
            setUser(updated);
            setOriginalUser(updated);
            setEditMode(false);
            setNewPassword('');
            setConfirmPassword('');
            toast.success('User updated');
        } catch (e) {
            toast.error('Failed to update user');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (originalUser) {
            setUser(originalUser);
        }
        setEditMode(false);
        setNewPassword('');
        setConfirmPassword('');
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    User Overview
                </Typography>
                {!editMode ? (
                    <Button variant="contained" onClick={() => setEditMode(true)}>Edit</Button>
                ) : (
                    <Box display="flex" gap={1}>
                        <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                    </Box>
                )}
            </Box>

            <Grid container spacing={3}>


                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>User Details</Typography>
                            {!editMode ? (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}><Typography><strong>ID:</strong> {user._id}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>First Name:</strong> {user.firstName || '-'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Last Name:</strong> {user.lastName || '-'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Email:</strong> {user.email || '-'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Phone:</strong> {user.phone || '-'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Company:</strong> {user.company || '-'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Profile Image:</strong> {user.profileImage || '-'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Role:</strong> <Chip label={user.role} size="small" color={user.role === 'superadmin' ? 'error' : (user.role === 'admin' ? 'primary' : 'default')} /></Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Currency:</strong> {user.settings?.currency || 'USD'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Email Verified:</strong> {user.emailVerified ? 'Yes' : 'No'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography><strong>Active:</strong> {user.isActive ? 'Yes' : 'No'}</Typography></Grid>
                                </Grid>
                            ) : (
                                <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="First Name" fullWidth value={user.firstName || ''} onChange={(e) => setUser({ ...user, firstName: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Last Name" fullWidth value={user.lastName || ''} onChange={(e) => setUser({ ...user, lastName: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Email" type="email" fullWidth value={user.email || ''} onChange={(e) => setUser({ ...user, email: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Phone" fullWidth value={user.phone || ''} onChange={(e) => setUser({ ...user, phone: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Company" fullWidth value={user.company || ''} onChange={(e) => setUser({ ...user, company: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Profile Image URL" fullWidth value={user.profileImage || ''} onChange={(e) => setUser({ ...user, profileImage: e.target.value })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Role" select fullWidth value={user.role || 'client'} onChange={(e) => setUser({ ...user, role: e.target.value })}>
                                                <MenuItem value="client">client</MenuItem>
                                                <MenuItem value="admin">admin</MenuItem>
                                                <MenuItem value="superadmin">superadmin</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Currency" select fullWidth value={user.settings?.currency || 'USD'} onChange={(e) => setUser({ ...user, settings: { ...user.settings, currency: e.target.value } })}>
                                                <MenuItem value="USD">USD</MenuItem>
                                                <MenuItem value="GBP">GBP</MenuItem>
                                                <MenuItem value="PKR">PKR</MenuItem>
                                                <MenuItem value="INR">INR</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel control={<Switch checked={!!user.emailVerified} onChange={(e) => setUser({ ...user, emailVerified: e.target.checked })} />} label="Email Verified" />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel control={<Switch checked={!!user.isActive} onChange={(e) => setUser({ ...user, isActive: e.target.checked })} />} label="Active" />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="New Password"
                                                fullWidth
                                                type={showNewPassword ? 'text' : 'password'}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                helperText="Leave blank to keep the current password"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                label="Confirm New Password"
                                                fullWidth
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" gutterBottom>Address</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Street" fullWidth value={user.address?.street || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, street: e.target.value } })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="City" fullWidth value={user.address?.city || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, city: e.target.value } })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="State" fullWidth value={user.address?.state || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, state: e.target.value } })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Zip Code" fullWidth value={user.address?.zipCode || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, zipCode: e.target.value } })} />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField label="Country" fullWidth value={user.address?.country || ''} onChange={(e) => setUser({ ...user, address: { ...user.address, country: e.target.value } })} />
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>






                {/* Core Website Details summary with edit-like UI (note: saving requires opening website detail) */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Core Website Details</Typography>
                            <Grid container spacing={2}>
                                {(overview.websites || []).slice(0, 3).map((w) => (
                                    <React.Fragment key={w._id}>
                                        <Grid item xs={12} sm={6}><Typography><strong>Name:</strong> {w.name}</Typography></Grid>
                                        <Grid item xs={12} sm={6}><Typography><strong>Site ID:</strong> {w.siteId}</Typography></Grid>
                                        <Grid item xs={12} sm={6}><Typography><strong>Domain:</strong> {w.domain || '-'}</Typography></Grid>
                                        <Grid item xs={12} sm={6} display="flex" alignItems="center" justifyContent="space-between">
                                            <Typography><strong>Status:</strong> {w.status || 'active'}</Typography>
                                            <Button size="small" variant="outlined" onClick={() => navigate(`/dashboard/superadmin/websites/${w._id}`)}>Edit</Button>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6} lg={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Schemas ({overview.schemas?.length || 0})</Typography>
                            <Divider sx={{ my: 1 }} />
                            <List dense>
                                {(overview.schemas || []).slice(0, 5).map((s) => (
                                    <ListItem key={s._id} sx={{ px: 0 }}>
                                        <ListItemText primary={s.name} secondary={s.slug} />
                                    </ListItem>
                                ))}
                            </List>
                            {overview.schemas?.length > 5 && (
                                <Chip label={`+${overview.schemas.length - 5} more`} size="small" />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Orders</Typography>
                            <Divider sx={{ my: 1 }} />
                            {ordersLoading ? (
                                <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
                            ) : (
                                <>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Order #</TableCell>
                                                <TableCell>Customer</TableCell>
                                                <TableCell>Website</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Payment</TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {ordersData.map((o) => (
                                                <TableRow key={o._id} hover>
                                                    <TableCell>#{o.orderNumber}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight={600}>{o.customerName || '-'}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{o.customerEmail || '-'}</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{o.website?.name || '-'}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{o.website?.siteId || '-'}</Typography>
                                                    </TableCell>
                                                    <TableCell align="right">${Number(o.totalAmount || 0).toFixed(2)}</TableCell>
                                                    <TableCell>{o.status}</TableCell>
                                                    <TableCell>{o.paymentStatus}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">{dayjs(o.createdAt).format('YYYY-MM-DD HH:mm')}</Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="Open">
                                                            <IconButton size="small" onClick={() => window.open(`/dashboard/orders?focus=${o._id}`, '_blank')}>
                                                                <LaunchIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" color="error" onClick={() => deleteOrder(o._id)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {ordersData.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center">
                                                        <Typography variant="body2" color="text.secondary">No orders found</Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={ordersTotal}
                                        page={ordersPage}
                                        onPageChange={onOrdersPageChange}
                                        rowsPerPage={ordersRowsPerPage}
                                        onRowsPerPageChange={onOrdersRowsChange}
                                        rowsPerPageOptions={[10, 25, 50, 100]}
                                    />
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Recent Submissions ({overview.submissions?.length || 0})</Typography>
                            <Divider sx={{ my: 1 }} />
                            <List dense>
                                {(overview.submissions || []).slice(0, 10).map((s) => (
                                    <ListItem key={s._id} sx={{ px: 0 }}>
                                        <ListItemText primary={`${s.status}`} secondary={new Date(s.createdAt).toLocaleString()} />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SuperAdminUserDetailPage;


